import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useToastContext } from "components/alert/ToastContext";
import Base from "components/Base";
import { BaseModel } from "model/BaseModel";
import Pageable from "model/page/Pageable";
import Cte from "model/transportes/cte/Cte";
import { Button } from "primereact/button";
import { Column } from "primereact/column";
import { confirmDialog } from "primereact/confirmdialog";
import { DataTable, DataTableStateEvent } from "primereact/datatable";
import { FileUpload, FileUploadHandlerEvent } from "primereact/fileupload";
import { useLoadingContext } from "provider/LoadingProvider";
import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import CteService from "service/transportes/CteService";
import { converterDataHora, numberToCnpj } from "utils/convert";
import saveAs from 'file-saver';
import { DataTableFilterMeta } from "primereact/datatable";
import { FilterMatchMode, FilterOperator } from "primereact/api";
import BaseListagem from "components/BaseListagem";
import { DateMatchModeOptions, IdMatchModeOptions, NumberMatchModeOptions, TextMatchModeOptions, filterApplyTemplate, numberFilterTemplate, dateRangeFilterTemplate } from "utils/filterUtils";
import { useFilter } from "contexts/FilterContext";

const cteService = new CteService();

export default function CteListagem() {

    const [queryParameters] = useSearchParams();
    const { startLoading, stopLoading } = useLoadingContext();
    const navigate = useNavigate();
    const tituloPagina = 'CTE';

    const [filtrando, setFiltrando] = useState<boolean>(false);
    const [rows, setRows] = useState<Cte[]>([]);
    const [pageable, setPageable] = useState(new Pageable(0, 10, "chCte,ASC", 0));
    const [totalElements, setTotalElements] = useState<number>(0);
    const [excluindo, setExcluindo] = useState<boolean>(false);
    const [idConta] = useState<number | null>(queryParameters.has('idConta') ? parseInt(queryParameters.get('idConta') || '0') : null);
    const [titulo, setTitulo] = useState<string>('Cte');

    const { msgSucesso, msgErro, msgAlerta } = useToastContext();

    const fileUploadRef = useRef<FileUpload>(null);

    const defaultFilters: DataTableFilterMeta = {
        chCte: {
            constraints: [
                {
                    value: '',
                    matchMode: FilterMatchMode.CONTAINS,
                }
            ],
            operator: FilterOperator.AND
        },
        serie: {
            constraints: [
                {
                    value: '',
                    matchMode: FilterMatchMode.CONTAINS,
                }
            ],
            operator: FilterOperator.AND
        },
        nct: {
            constraints: [
                {
                    value: '',
                    matchMode: FilterMatchMode.EQUALS,
                }
            ],
            operator: FilterOperator.AND
        },
        cnpjDestinatario: {
            constraints: [
                {
                    value: '',
                    matchMode: FilterMatchMode.CONTAINS,
                }
            ],
            operator: FilterOperator.AND
        },
        cnpjRemetente: {
            constraints: [
                {
                    value: '',
                    matchMode: FilterMatchMode.CONTAINS,
                }
            ],
            operator: FilterOperator.AND
        },
        xmunIni: {
            constraints: [
                {
                    value: '',
                    matchMode: FilterMatchMode.CONTAINS,
                }
            ],
            operator: FilterOperator.AND
        },
        xmunFim: {
            constraints: [
                {
                    value: '',
                    matchMode: FilterMatchMode.CONTAINS,
                }
            ],
            operator: FilterOperator.AND
        },
        dhEmi: {
            constraints: [
                {
                    value: null,
                    matchMode: FilterMatchMode.EQUALS,
                }
            ],
            operator: FilterOperator.AND
        },
    }

    const filterContext = useFilter();
    const screen = "cte";
    const [filters, setFilters] = useState<DataTableFilterMeta>(filterContext.filters[screen] ? filterContext.filters[screen] : defaultFilters);


    useEffect(() => {
        setFiltrando(true);
    }, [filters]);

    useEffect(() => {
        if (filtrando) {
            cteService
                .pesquisar(pageable, filters)
                .then(lista => {
                    setFiltrando(false);
                    setRows(lista.content);
                    setTotalElements(lista.totalElements);
                })
                .catch(err => {
                    setFiltrando(false);
                });
        }
    }, [filtrando, pageable]);

    const onPage = (event: DataTableStateEvent) => {

        setPageable({ ...pageable, page: event.page !== undefined ? event.page : 0, first: event.first });
        setFiltrando(true);

    };

    const handleUpload = (event: FileUploadHandlerEvent) => {
        const files: File[] = event.files;

        const allFileContents: string[] = [];
        let filesProcessed = 0;

        const readFile = (file: File) => {
            const reader = new FileReader();

            reader.onload = (e: ProgressEvent<FileReader>) => {
                const xmlContent = e.target?.result as string;
                allFileContents.push(xmlContent);

                filesProcessed += 1;

                if (filesProcessed === files.length) {
                    startLoading();
                    cteService
                        .importarXml(allFileContents)
                        .then(() => {
                            stopLoading();
                            setFiltrando(true);
                            msgSucesso(tituloPagina, 'XML importado com sucesso!');
                        })
                        .catch(error => {
                            stopLoading();
                            console.error(error);
                            setFiltrando(true);
                            if (error && error.data && error.data && error.data.message) {
                                msgErro(tituloPagina, error.data.message);
                            } else {
                                msgErro(tituloPagina, 'Ocorreu um erro ao tentar importar o XML, tente novamente!');
                            }

                        });
                    if (fileUploadRef.current) {
                        fileUploadRef.current.clear();
                    }
                }
            };
            reader.readAsText(file);
        };

        files.forEach(file => readFile(file));
    };

    function editar(item: BaseModel) {
        let identificador = item.id ? item.id : item.codigo;
        let url = `editar/${identificador}`;
        if (idConta) {
            url += `?idConta=${idConta}`;
        }
        navigate(`${url}`);
    }

    function remover(item: BaseModel) {
        confirmDialog({
            acceptLabel: 'Sim',
            rejectLabel: 'Não',
            message: 'Deseja realmente excluir este registro?',
            header: 'Confirmação',
            icon: 'pi pi-exclamation-triangle',
            accept: () => removerConfirm(item),
        });
    }

    function removerConfirm(item: BaseModel) {
        setExcluindo(true);
        let identificador = item.id ? item.id : item.codigo;
        if (identificador) {

            cteService
                .excluir(Number(identificador))
                .then(() => {
                    setExcluindo(false);
                    setFiltrando(true);
                    msgSucesso(tituloPagina, 'Registro removido com sucesso!');
                })
                .catch(error => {
                    setExcluindo(false);
                    console.error(error);
                    if (error && error.data && error.data.message) {
                        msgErro(tituloPagina, error.data.message);
                    } else {
                        msgErro(tituloPagina, 'Ocorreu um erro ao tentar remover o registro, tente novamente!');
                    }

                });
        }
    }

    const gerarDacte = (cte: Cte) => {
        startLoading();
        cteService
            .getDacte(cte.id as number)
            .then((data) => {
                stopLoading();
                const file = new Blob([data], { type: 'application/pdf' });
                saveAs(file, `DACTE-${cte.nct}.pdf`);
            })
            .catch(error => {
                stopLoading();
                console.error(error);
                if (error && error.data && error.data.message) {
                    msgErro(tituloPagina, error.data.message);
                } else {
                    msgErro(tituloPagina, 'Ocorreu um erro ao tentar gerar o DACTE, tente novamente!');
                }
            });
    }


    const header = <div style={{ display: "flex", justifyContent: "space-between", textAlign: 'center', alignContent: "center" }}>
        <div></div>
        <span style={{ textAlign: 'center', alignSelf: "center", position: 'relative' }}>{tituloPagina}</span>
        <div >
            <FileUpload
                ref={fileUploadRef}
                mode="basic"
                accept="text/xml"
                maxFileSize={1000000}
                customUpload
                uploadHandler={handleUpload}
                auto
                chooseLabel="Importar XML"
                multiple />
        </div>
    </div>

    return (
        <Base tituloPagina={tituloPagina}>
            <DataTable
                header={header}
                value={rows} lazy dataKey="id" paginator
                first={pageable.first} rows={10} totalRecords={totalElements} onPage={onPage}
                loading={filtrando} tableStyle={{ minWidth: '75rem' }}
                filters={filters} onFilter={(e) => setFilters(e.filters)}
                emptyMessage="Nenhum registro encontrado"
            >
                <Column field="chCte" filter header="Chave CTE" filterField="chCte" filterMatchModeOptions={TextMatchModeOptions} showFilterOperator={false} showAddButton={false} filterApply={filterApplyTemplate}></Column>
                <Column field="serie" filter header="Série" filterField="serie" filterMatchModeOptions={TextMatchModeOptions} showFilterOperator={false} showAddButton={false} filterApply={filterApplyTemplate}></Column>
                <Column field="nct" filter header="Número" filterField="nct" filterMatchModeOptions={NumberMatchModeOptions} showFilterOperator={false} showAddButton={false} filterApply={filterApplyTemplate}></Column>
                <Column field="dhEmi" filter header="Data emissão" body={(rowData) => converterDataHora(rowData.dhEmi)}
                    filterMatchModeOptions={DateMatchModeOptions} filterElement={dateRangeFilterTemplate} showFilterMenuOptions={false} showFilterOperator={false} showAddButton={false} filterField="dhEmi" filterApply={filterApplyTemplate} filterMenuStyle={{ zIndex: 50 }}></Column>
                <Column field="remetente.cnpj" filter header="Remetente" body={(rowData) => numberToCnpj(rowData.remetente.cnpj)} filterMatchModeOptions={TextMatchModeOptions}
                    showFilterOperator={false} showAddButton={false} filterField="cnpjRemetente" filterApply={filterApplyTemplate}></Column>
                <Column field="destinatario.cnpj" filter header="Destinatário" body={(rowData) => numberToCnpj(rowData.destinatario.cnpj)} filterMatchModeOptions={TextMatchModeOptions} showFilterOperator={false} showAddButton={false} filterField="cnpjDestinatario" filterApply={filterApplyTemplate}></Column>
                <Column field="xmunIni" filter header="Município de início" filterField="xmunIni" showFilterOperator={false} filterMatchModeOptions={TextMatchModeOptions} showAddButton={false} filterApply={filterApplyTemplate} />
                <Column field="xmunFim" filter header="Município de Fim" filterField="xmunFim" showFilterOperator={false} filterMatchModeOptions={TextMatchModeOptions} showAddButton={false} filterApply={filterApplyTemplate} />
                <Column key="dacte" style={{ whiteSpace: 'nowrap', width: '7em' }} body={(rowData) => <div>
                    <Button type="button" label="DACTE" icon="pi pi-file-pdf" className="ui-button-info" onClick={() => gerarDacte(rowData)} />
                </div>} />
                <Column key="colOpcoes" style={{ whiteSpace: 'nowrap', width: '7em' }}
                    body={(item) => <div>
                        <Button type="button" icon={<FontAwesomeIcon icon="edit" />} className="ui-button-info" onClick={() => editar(item)} />
                        <Button type="button" icon={<FontAwesomeIcon icon="remove" />} className="ui-button-danger" style={{ marginLeft: 8 }} onClick={() => remover(item)} />
                    </div>}
                />
            </DataTable>
        </Base>
    )
}